import { Component, computed, effect, inject, NgZone, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { Visitor } from '@app/common-codes/models/matwins-tenant/Visitor.model';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { PhotoCaptureComponent } from '../../ipad/photo/photo-capture.component';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { VISITOR_TYPES, VisitorType } from '../../ipad/ipad-visitor-type/ipad-visitor-type.component';
import { CheckinBaseComponent } from '../../ipad/checkin/checkin-base.component';
import { HealthyViewerComponent } from '../../ipad/checkin/healthy/healthy-viewer.component';
import { QuizViewerComponent } from '../../ipad/checkin/quiz/quiz-viewer.component';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { PolicyViewerComponent } from '../../ipad/checkin/policy/policy-viewer.component';
import { EngagementViewerComponent } from '../../ipad/checkin/contractor-checkin/engagement-viewer.component';
import { SignaturePadComponent } from '@app/common-codes/components/nested-components/signature-pad/signature-pad.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { visitTypeIdOf } from '../../shared/visitor-types';

/**
 * Check-in visiteur en page unique pour le flux QR Code.
 * Affiche les champs visiteur (nom, compagnie, employé visité) puis,
 * en dessous, tous les documents à signer (politiques, engagements, covid, quiz)
 * selon la configuration du PointOfService et le type de visiteur (:type).
 */
@Register(Visit)
@Component({
    selector: 'app-qrcode-visitor-checkin',
    templateUrl: './qrcode-visitor-checkin.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, TranslatePipe, OnReadyDirective, FieldWrapperComponent, PhotoCaptureComponent, MultiSelectComponent, PolicyViewerComponent, EngagementViewerComponent, HealthyViewerComponent, QuizViewerComponent, SignaturePadComponent]
})
export class QrcodeVisitorCheckinComponent extends CheckinBaseComponent {

    override Skin: IStaticStylesConfig = MatwinsSkins.QrCode1;
    override entityMetaData = new Visit().GetType();

    // Page QR Code publique : pas de permissions requises (comme le flow trucker)
    public override get RequiredPermissions(): string[] {
        return [];
    }
    public override get HasRequiredPermissions(): boolean {
        return true;
    }

    visitorType: VisitorType | null = null;

    private variables = inject(MatwinsVariablesService);

    /**
     * Le visiteur est identifié : soit créé (nouveau), soit existant et vérifié
     * (dernière visite chargée par onVisitorSelectChange). Les sections de
     * documents et les actions ne s'affichent qu'à partir de ce moment.
     */
    visitorIdentified = computed(() => {
        const e = this.editingEntity();
        return !!e?.visitor?.fullname || !!e?.fK_Visitor;
    });

    /**
     * Steps auto-skippées par le validator, figées au moment de l'identification
     * du visiteur (ex. politique déjà signée lors d'une visite précédente).
     * On ne réévalue pas en live : canAutoSkip redeviendrait true dès que
     * l'utilisateur remplit la section (allCovidChecked, quizStatus...) et
     * la section disparaîtrait sous ses yeux.
     */
    autoSkippedSteps = signal<Set<string>>(new Set());

    /** Steps visibles selon la config POS (le validator est rebuild quand le POS arrive). */
    visibleSteps = computed(() => {
        const pos = this.tenantService.currentPointOfService() ?? undefined;
        this.editingEntity();
        // rebuild ici aussi : le validator garde pos en interne (non réactif)
        this.stepValidator.rebuild(this.visitorType, pos);
        return this.stepValidator.getVisibleSteps();
    });

    /**
     * Une signature unique en bas du formulaire est requise dès qu'un document
     * à signer est affiché (politique, engagement, santé) et non auto-skippé.
     */
    signatureRequired = computed(() => {
        const entity = this.editingEntity();
        if (!entity || !this.visitorIdentified()) return false;
        const skipped = this.autoSkippedSteps();
        return this.visibleSteps().some(s =>
            (s.fields.some(f => f.fieldName === 'signature') || s.stepId.includes('healthy'))
            && !skipped.has(s.stepId));
    });

    /** Toutes les sections visibles sont valides (ou auto-skippées car données déjà présentes). */
    canSubmit = computed(() => {
        const entity = this.editingEntity();
        const pos = this.tenantService.currentPointOfService() ?? undefined;
        if (!entity) return false;
        const stepsValid = this.visibleSteps().every(s =>
            s.canAutoSkip(entity, pos) || s.fields.every(f => f.validate(entity, pos)));
        if (!stepsValid) return false;
        if (this.signatureRequired()) {
            return !!(entity.signatureFile || entity.fK_Image_Signature);
        }
        return true;
    });

    isEmployee = computed(() => this.visitorType?.key === 'employee');
    isVisitor = computed(() => this.visitorType?.key === 'visitor');
    isContractor = computed(() => this.visitorType?.key === 'contractor');

    showCompany = computed(() => !this.isEmployee());
    showPhone = computed(() => {
        const pos = this.tenantService.currentPointOfService();
        if (this.isVisitor()) return !!pos?.dS_NewVisitor_ShowPhoneNumber;
        if (this.isContractor()) return !!pos?.dS_NewContractor_ShowPhoneNumber;
        return false;
    });
    showCamera = computed(() => {
        const pos = this.tenantService.currentPointOfService();
        if (this.isVisitor()) return !!pos?.dS_NewVisitor_Camera;
        if (this.isContractor()) return true;
        return false;
    });

    /** Pour le type employé : visiteurs historiques + employés fusionnés (comme iPad). */
    employeeVisitorOptions = computed(() => {
        const visitors = this.visitorsLookup();
        const employees = this.employeesLookup();

        const map = new Map<string, LookupItem>();

        for (const v of visitors) {
            const key = v.value.trim().toLowerCase();
            if (!map.has(key)) {
                map.set(key, { ...v, additionalData: { ...v.additionalData, _lookupSource: 'visitor' } });
            }
        }

        for (const e of employees) {
            const key = e.value.trim().toLowerCase();
            if (!map.has(key)) {
                const prefixedId = `emp_${e.id}`;
                map.set(key, {
                    id: prefixedId,
                    key: prefixedId,
                    value: e.value,
                    sortOrder: e.sortOrder,
                    visible: e.visible,
                    additionalData: { ...e.additionalData, _lookupSource: 'employee', _originalId: e.id },
                    children: e.children,
                    selectedValue: e.selectedValue
                });
            }
        }

        return Array.from(map.values());
    });

    visitorOptions = computed(() => {
        return this.isEmployee() ? this.employeeVisitorOptions() : this.visitorsLookup();
    });

    constructor(
        route: ActivatedRoute,
        router: Router,
        translationService: TranslationService,
        validationService: GenericValidationService,
        ngZone: NgZone,
        navService: NavigationService,
        tenantService: CurrentTenantService,
        alertModalService: AlertModalService
    ) {
        super(route, router, translationService, validationService, ngZone, navService, tenantService, alertModalService);

        // Le POS peut arriver après ngOnInit (deep-link direct sur la page) :
        // recharger les lookups filtrés par POS et l'URL de la politique.
        effect(() => {
            const pos = this.tenantService.currentPointOfService();
            if (pos) {
                if (!this.visitorLookupProfileLoaded) {
                    this.loadLookups();
                }
                this.initPolicies();
                const entity = this.editingEntity();
                if (entity) {
                    // getEmptyEntity() a pu tourner avant le chargement du POS :
                    // FK_PointOfService=0 provoquerait une violation de FK au checkin.
                    if (!entity.fK_PointOfService) {
                        entity.fK_PointOfService = pos.id;
                    }
                    // Si le visiteur était déjà identifié avant l'arrivée du POS,
                    // recalculer les steps skippées tant que rien n'a été saisi.
                    if (!this.hasDocumentData(entity)) {
                        this.refreshAutoSkippedSteps();
                    }
                }
            }
        });
    }

    override async ngOnInit(): Promise<void> {
        const typeKey = this.route.snapshot.paramMap.get('type') ?? 'visitor';
        this.visitorType = VISITOR_TYPES.find(vt => vt.key === typeKey) ?? VISITOR_TYPES[0];
        await super.ngOnInit();
    }

    /**
     * Une section de document s'affiche si :
     * - le visiteur est identifié (créé ou vérifié via sa dernière visite),
     * - la step est visible selon la config POS,
     * - la step n'était pas auto-skippable au moment de la vérification
     *   (ex. politique déjà signée récemment → pas besoin de la re-signer).
     */
    isStepVisible(stepIdPart: string): boolean {
        if (!this.visitorIdentified()) return false;
        const skipped = this.autoSkippedSteps();
        return this.visibleSteps().some(s => s.stepId.includes(stepIdPart) && !skipped.has(s.stepId));
    }

    /** Recalcule (et fige) les steps auto-skippées pour le visiteur identifié. */
    private refreshAutoSkippedSteps(): void {
        const entity = this.editingEntity();
        const pos = this.tenantService.currentPointOfService() ?? undefined;
        this.stepValidator.rebuild(this.visitorType, pos);
        const skipped = new Set<string>();
        if (entity && this.visitorIdentified()) {
            // canAutoSkip ne doit refléter que les données pré-existantes
            // (dernière visite), jamais la saisie courante du formulaire.
            const evalEntity = Object.assign(new Visit(), entity);
            evalEntity.signatureFile = undefined;
            evalEntity.allCovidChecked = undefined;
            evalEntity.allEngamgementChecked = undefined;
            evalEntity.quizStatus = undefined;
            evalEntity.symptoms = undefined;
            for (const s of this.stepValidator.getVisibleSteps()) {
                if (s.canAutoSkip(evalEntity, pos)) skipped.add(s.stepId);
            }
        }
        this.autoSkippedSteps.set(skipped);
    }

    /** true si l'utilisateur a déjà saisi des données dans une section document. */
    private hasDocumentData(e: Visit): boolean {
        return !!(e.signatureFile || e.allCovidChecked || e.allEngamgementChecked
            || e.quizStatus != null || this.policyAccepted);
    }

    /** Purge les données de signature/politique héritées du visiteur précédent. */
    private resetSignedDocumentData(visit: Visit): void {
        visit.signatureFile = undefined;
        visit.fK_Image_Signature = undefined;
        visit.agreementsDate = undefined;
        visit.fK_PolicyRdlc = undefined;
        visit.fK_PolicyPdf = undefined;
        visit.previousVisit = undefined;
        this.policyAccepted = false;
    }

    override async onVisitorSelectChange(ids: string[]): Promise<void> {
        const visit = this.editingEntity();
        if (!visit) return;

        // Une signature dessinée appartient au visiteur précédent : on la purge.
        this.resetSignedDocumentData(visit);

        if (!ids?.length) {
            // Désélection → retour à l'état non identifié
            visit.fK_Visitor = 0;
            visit.visitor = new Visitor();
            visit.visitor.currentLanguage = this.translationService?.currentLanguageId;
            this.editingEntity.set(Object.assign(new Visit(), visit));
            this.autoSkippedSteps.set(new Set());
            return;
        }

        // Vérification du visiteur existant (chargement de la dernière visite)
        // AVANT d'afficher les actions.
        this.loaderService.show();
        try {
            await super.onVisitorSelectChange(ids);
        } finally {
            this.loaderService.hide();
        }
        this.refreshAutoSkippedSteps();
    }

    override onCreateVisitor(fullname: string): void {
        const visit = this.editingEntity();
        if (visit) {
            this.resetSignedDocumentData(visit);
        }
        super.onCreateVisitor(fullname);
        this.refreshAutoSkippedSteps();
    }

    override getEmptyEntity(): Visit {
        const visit = super.getEmptyEntity();
        visit.visitorType = visitTypeIdOf(this.visitorType?.key);
        return visit;
    }

    // --- Sections de documents ---

    protected override supportsEngagement = true;

    // Signature unique : les visionneuses n'en portent pas.
    protected override applyHealthySignature(): void {
    }

    protected override applyEngagementSignature(): void {
    }

    // --- Sauvegarde / navigation ---

    override async saveCheckin(): Promise<void> {
        const visit = this.editingEntity();
        if (!visit) return;

        // Sécurité : le POS devait être renseigné à la création de l'entité,
        // mais il charge en async dans le layout QR.
        if (!visit.fK_PointOfService) {
            visit.fK_PointOfService = this.tenantService.currentPointOfServiceId() || 0;
        }

        const savedVisit = await this.postCheckin(visit);
        if (savedVisit) {
            this.alertModalService.showToast(
                this.translationService.instant('vues.qr-code.checkin-form.welcome-site'),
                ToastType.Success,
                5000,
                true
            );
            this.goHome();
        }
    }

    override goHome() {
        const route = `/qrcode/${this.variables.clientId}/${this.variables.posId}/home`;
        this.navService.navigateTo(route);
    }
}
